import { ApolloError } from '@apollo/client';
import {
  HHButton,
  HHStack,
  HHTextField,
  HHTypography,
} from '@hinge-health/react-component-library';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { Autocomplete } from '@mui/material';
import { SystemStyleObject, Theme } from '@mui/system';
import { JSX } from 'react';
import { BatchClientInsurerType } from '../../types';

export interface ClientInsurerSearchProps {
  sortedClientInsurers: BatchClientInsurerType[];
  clientInsurersLoading: boolean;
  clientInsurersError?: ApolloError;
  handleSearch: (
    e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>,
  ) => void;
  setClientInsurerId: (clientInsurerId: string) => void;
  isClientInsurerDisabled: boolean;
  titleColor?: string;
  compactMode?: boolean;
}

export const ClientInsurerSearch = ({
  sortedClientInsurers,
  clientInsurersLoading,
  clientInsurersError,
  handleSearch,
  setClientInsurerId,
  isClientInsurerDisabled,
  titleColor,
  compactMode,
}: ClientInsurerSearchProps): JSX.Element => (
  <HHStack
    border="1px solid lightgray"
    borderRadius={1}
    spacing={2}
    padding={4}
    sx={(theme): SystemStyleObject<Theme> => ({
      backgroundColor: theme.palette.background.default,
    })}
  >
    <HHTypography hhVariant="body" color={titleColor ?? ''}>
      Client Insurers
    </HHTypography>
    <form onSubmit={handleSearch}>
      <HHStack direction="row" spacing={2}>
        <Autocomplete
          freeSolo
          options={clientInsurersError ? [] : sortedClientInsurers}
          loading={clientInsurersLoading}
          getOptionLabel={(option: BatchClientInsurerType | string): string =>
            typeof option === 'string'
              ? option
              : `${option.client.name} - ${option.insurer.name} (${option.id})`
          }
          isOptionEqualToValue={(
            option: BatchClientInsurerType,
            value: BatchClientInsurerType,
          ): boolean => option.id === value.id}
          filterOptions={(options, params): BatchClientInsurerType[] =>
            options.filter(
              option =>
                option.client.name
                  .toLowerCase()
                  .includes(params.inputValue.toLowerCase()) ||
                option.insurer.name
                  .toLowerCase()
                  .includes(params.inputValue.toLowerCase()) ||
                option.id.toString().includes(params.inputValue),
            )
          }
          size="small"
          onInputChange={(e, newInputValue): void => {
            if (!Number.isNaN(Number(newInputValue))) {
              setClientInsurerId(newInputValue);
            }
          }}
          onChange={(
            _,
            newValue: BatchClientInsurerType | string | null,
          ): void => {
            if (newValue && typeof newValue === 'object') {
              setClientInsurerId(newValue?.id.toString() ?? '');
            }
          }}
          sx={{ minWidth: 150 }}
          fullWidth
          renderInput={(params): JSX.Element => (
            <HHTextField
              {...params}
              hhVariant="variant-bypass"
              variant="outlined"
              placeholder={'Search by Name or ID'}
              error={!!clientInsurersError}
              helperText={
                clientInsurersError
                  ? 'Error fetching client insurers'
                  : undefined
              }
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <SearchOutlinedIcon
                    sx={{
                      color: 'action.active',
                    }}
                  />
                ),
              }}
            />
          )}
        />
        {!compactMode && (
          <HHButton
            hhVariant="contained"
            aria-label="submit"
            type="submit"
            disabled={isClientInsurerDisabled}
            onClick={handleSearch}
          >
            Search
          </HHButton>
        )}
      </HHStack>
    </form>
  </HHStack>
);
